import Responsive from 'app/components/responsive';
import type { SelectProps } from 'app/design/primitives/select';
import { Select } from 'app/design/primitives/select';
import { getI18nLanguage } from 'app/lib/language';
import { getCountriesByLanguage } from 'app/localization/countries';
import { useTranslation } from 'react-i18next';
import { SelectPickerHtml } from './select-html';
import { isAddressCountryAllowed } from 'app/lib/address-region';

type CountrySelectProps = Omit<SelectProps<string>, 'options'> & {
  accountCountry?: string | null;
};

export function CountrySelect({ accountCountry, ...props }: CountrySelectProps) {
  const { t } = useTranslation('common');
  const i18nLanguage = getI18nLanguage();

  const favourites = ['BE', 'CA', 'FR', 'US'];
  const allCountries = getCountriesByLanguage(i18nLanguage);

  const countryToOption = (c: (typeof allCountries)[number]) => ({
    label: c.name,
    value: c.code,
  });

  const availableCountries = allCountries.filter((country) => isAddressCountryAllowed(accountCountry, country.code));
  const favouriteCountries = availableCountries.filter((c) => favourites.includes(c.code));
  const countrySelectionDisabled = props.disabled || (accountCountry?.toUpperCase() === 'US' && Boolean(props.value));

  const selectedCountry = (() => {
    if (!props.value || !allCountries.length) return undefined;
    return allCountries.find((country) => country.code === props.value);
  })();

  const options: SelectProps<string>['options'] = [
    {
      label: t('favourites'),
      values: favouriteCountries.map(countryToOption),
    },
    {
      label: t('otherCountries'),
      values: availableCountries.map(countryToOption),
    },
  ];

  return (
    <Responsive
      desktop={
        <SelectPickerHtml
          searchable
          disabled={countrySelectionDisabled}
          data={availableCountries.map((country) => ({
            key: country.code,
            value: country.name,
          }))}
          placeholder={t('countryFieldLabel', { ns: 'my-address' })}
          setSelected={(value) => {
            props.onChange(value);
          }}
          defaultOption={
            selectedCountry
              ? {
                  key: selectedCountry.code,
                  value: selectedCountry.name,
                }
              : undefined
          }
        />
      }
      mobile={<Select {...props} disabled={countrySelectionDisabled} {...{ options }} />}
    />
  );
}
